/**
 * Quando un periodo e' ancora aperto a un nuovo inventario.
 *
 * La condizione stava inline nel modale e diceva «tutto cio' che non e' una
 * bozza blocca». Cosi' un inventario **rifiutato** chiudeva il periodo per
 * sempre, mentre correggere le associazioni e reinviare e' l'unico modo di
 * rimediare a un rifiuto previsto dall'api SP ZDHC (v1.5 §05, tabella regole
 * §07): un declined non si riapre, se ne manda uno nuovo.
 */

/**
 * Gli stati che chiudono il periodo, scritti **per esclusione** e non per
 * elenco.
 *
 * La differenza non e' stilistica. I record legacy — i pdf generati prima del
 * Gateway — hanno `status` null e devono continuare a bloccare. Un elenco di
 * stati finalizzati (`['submitted','published'].includes(i.status)`) su un null
 * risponde `false` e li lascerebbe passare: il periodo di un report gia'
 * pubblicato tornerebbe modificabile. Escludendo invece i due stati che *non*
 * bloccano, un null non e' ne' `draft` ne' `declined` e resta bloccante.
 */
const OPEN_STATUSES = ['draft', 'declined'];

/**
 * `true` se questo inventario tiene chiuso il suo periodo.
 *
 * Bloccano `submitted` (in attesa di revisione), `published` (accettato) e i
 * legacy a `status` null. Non bloccano `draft`, ancora ritentabile, ne'
 * `declined`.
 */
export const freezesItsPeriod = (inventory) => ! OPEN_STATUSES.includes(inventory?.status);

/**
 * `true` se fra gli inventari dell'utente ce n'e' gia' uno che chiude il mese
 * di `date`.
 *
 * La domanda e' «esiste un pending o un accettato», **non** «esiste un
 * rifiutato». Dopo un reinvio il periodo ha sia il declined sia il nuovo
 * submitted: scritta come «c'e' un declined, quindi sblocca» resterebbe aperto
 * per sempre dopo il primo rifiuto.
 *
 * @param inventories la lista piatta dei tentativi dell'utente
 * @param date        il mese da aprire
 * @param isSameMonth (a, b) => bool — il confronto e' iniettato per tenere
 *                    questo modulo puro e privo di dipendenze da moment
 */
export const periodIsFrozen = (inventories, date, isSameMonth) =>
    (inventories ?? []).some((inventory) =>
        freezesItsPeriod(inventory) && isSameMonth(inventory.reference_date, date));
